import React, { useEffect, useMemo, useState } from 'react';
import { Col, Form, Row, Skeleton, Statistic, Table } from 'antd';
import { BarChartOutlined } from '@ant-design/icons';
import { Bar, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import PersianDatePicker from '../../components/PersianDatePicker';
import ReportPageLayout from './ReportPageLayout';
import ReportFiltersCard from './ReportFiltersCard';
import { reportsApi } from '../../utils/api';
import { getLast30DaysRange } from '../../utils/dateUtils';
import {
  extractRows,
  getDateRangePickerValue,
  parseDateRangeChange,
  toNumber,
} from './reportUtils';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import ChartEmptyState from './ChartEmptyState';
import { formatReportMoneyFa, formatReportTableAmount, REPORT_CHART_COLORS } from './chartFormatters';
import { IncomeExpenseTooltip } from './ReportChartTooltips';

const IncomeExpenseReport = () => {
  const range = getLast30DaysRange('jYYYY-MM-DD', 'jalali');
  const [filters, setFilters] = useState({ startDate: range.startDate, endDate: range.endDate });
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const load = async (nextFilters) => {
    setLoading(true);
    try {
      const res = await reportsApi.getIncomeExpenseReport(nextFilters);
      setRows(extractRows(res));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!filters.startDate || !filters.endDate) return;
    const timer = setTimeout(() => load(filters), 350);
    return () => clearTimeout(timer);
  }, [filters]);

  const chartData = useMemo(
    () =>
      rows.map((r, i) => ({
        label: r?.date || String(i + 1),
        income: toNumber(r?.income || r?.total_income),
        expense: toNumber(r?.expense || r?.total_expense),
      })),
    [rows],
  );

  const totals = useMemo(
    () =>
      chartData.reduce(
        (acc, item) => ({ income: acc.income + item.income, expense: acc.expense + item.expense }),
        { income: 0, expense: 0 },
      ),
    [chartData],
  );

  return (
    <ReportPageLayout
      headerIcon={<BarChartOutlined />}
      title="درآمد در مقابل هزینه"
      subtitle="مقایسه درآمد و هزینه "
    >
      <ReportFiltersCard>
        <Form layout="vertical" className="custom-font">
          <Row gutter={12}>
            <Col xs={24} md={12} lg={10}>
              <Form.Item label="بازه تاریخ" className="mb-0">
                <PersianDatePicker
                  range
                  placeholder="انتخاب بازه از تاریخ تا تاریخ"
                  value={getDateRangePickerValue(filters.startDate, filters.endDate)}
                  onChange={(dates) => setFilters((p) => ({ ...p, ...parseDateRangeChange(dates) }))}
                  style={{ minHeight: 36 }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </ReportFiltersCard>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
        <div className="rounded-xl border border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30 px-4 py-3">
          <Statistic
            title="جمع درآمد"
            value={totals.income}
            formatter={(v) => <span className="tabular-nums custom-font">{formatReportMoneyFa(v)}</span>}
          />
        </div>
        <div className="rounded-xl border border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30 px-4 py-3">
          <Statistic
            title="جمع هزینه"
            value={totals.expense}
            formatter={(v) => <span className="tabular-nums custom-font">{formatReportMoneyFa(v)}</span>}
          />
        </div>
      </div>

      <div className="mb-5">
        {loading && !chartData.length ? (
          <Skeleton active paragraph={{ rows: 8 }} />
        ) : chartData.length ? (
          <div className="h-60 w-full pl-0 sm:pl-1" dir="ltr">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 6, right: 10, left: 0, bottom: 4 }}>
                <XAxis dataKey="label" tick={{ fontSize: 11 }} tickMargin={6} interval="preserveStartEnd" />
                <YAxis
                  width={88}
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => formatReportMoneyFa(v)}
                  tickMargin={4}
                />
                <Tooltip content={<IncomeExpenseTooltip />} />
                <Legend wrapperStyle={{ direction: 'rtl', fontFamily: 'inherit' }} />
                <Bar dataKey="income" name="درآمد" fill={REPORT_CHART_COLORS.mint} radius={[4, 4, 0, 0]} />
                <Line
                  type="monotone"
                  dataKey="expense"
                  name="هزینه"
                  stroke={REPORT_CHART_COLORS.teal}
                  strokeWidth={2.25}
                  dot={{ r: 3, fill: REPORT_CHART_COLORS.teal }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <ChartEmptyState />
        )}
      </div>

      {loading && !rows.length ? (
        <TableSkeleton rows={6} columns={3} />
      ) : (
        <Table
          rowKey={(r, i) => r.id || i}
          loading={loading}
          dataSource={rows}
          scroll={{ x: 900 }}
          className="custom-font"
          columns={[
            { title: 'تاریخ', dataIndex: 'date' },
            { title: 'درآمد', render: (_, r) => formatReportTableAmount(r?.income ?? r?.total_income) },
            { title: 'هزینه', render: (_, r) => formatReportTableAmount(r?.expense ?? r?.total_expense) },
          ]}
        />
      )}
    </ReportPageLayout>
  );
};

export default IncomeExpenseReport;
